/**
 * API CONSTANTS
 * Shared so the backend and both frontends agree on paging rules and
 * error codes without anyone hardcoding a magic number.
 */

/**
 * Every route lives under this prefix.
 * Versioning matters: once students have the app open on their phones you
 * cannot force an update. If a breaking change is ever needed, we publish
 * /api/v2 and keep /api/v1 running until the old clients disappear.
 */
export const API_PREFIX = '/api/v1';

/**
 * Pagination defaults.
 *
 * MAX_LIMIT is a security control, not a preference. Without it, a request
 * for ?limit=9999999 would ask MongoDB to load the entire catalogue into
 * memory at once — a trivial way to take the server down.
 */
export const PAGINATION = Object.freeze({
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
});

/**
 * Machine-readable error codes.
 *
 * The frontend should branch on `code`, never on the human message.
 * Messages get rewritten and translated; codes are a stable contract.
 */
export const ERROR_CODE = Object.freeze({
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  DUPLICATE: 'DUPLICATE',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  DATABASE_ERROR: 'DATABASE_ERROR',
  BAD_REQUEST: 'BAD_REQUEST',
  /** A dependency we need is switched off or down — payments, Google, Mongo. */
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
});

/** HTTP status codes, named so the code reads as intent rather than digits. */
export const HTTP_STATUS = Object.freeze({
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
});
